/**
 * q93_soft_stop_logic.ts
 * BUILD-Q9.3-SUBQ — SOFT_STOP detection and RULE-ESC-1 eligibility
 * Locked: S-CP9-1, Fri May 15 2026 2:09 PM EDT
 *
 * SOFT_STOP routing for Q9.3.A-D paper-process sub-questions. Each SOFT_STOP
 * answer is RULE-ESC-1 eligible for manager override. SOFT_STOPs do NOT gate
 * CP9 from advancing — they accumulate as unresolved flags evaluated at CP10
 * exit verdict (LOG-02, threshold: 3).
 *
 * `selectedOption` storage matches the option `key` field on each question
 * (the bespoke key, e.g. "yes-named" / "no" / "complete" / "unclear"), not
 * the graded-scale numeric string used by the rest of CP9.
 */

export const Q93_SUBQ_KEYS = [
  "cp9_paper_procurement_contact",
  "cp9_redline_cycle_status",
  "cp9_security_it_review",
  "cp9_signing_authority",
] as const;

export type Q93SubqKey = (typeof Q93_SUBQ_KEYS)[number];

export type FlagType = "OK" | "AMBER" | "SOFT_STOP";

const Q93_FLAG_BY_OPTION_KEY: Record<string, Record<string, FlagType>> = {
  cp9_paper_procurement_contact: {
    "yes-named": "OK",
    "yes-role-only": "AMBER",
    no: "SOFT_STOP",
  },
  cp9_redline_cycle_status: {
    "yes-completed": "OK",
    "yes-in-progress": "OK",
    "no-not-started": "SOFT_STOP",
  },
  cp9_security_it_review: {
    complete: "OK",
    scheduled: "OK",
    pending: "AMBER",
    "not-required": "OK",
  },
  cp9_signing_authority: {
    "signed-name-known": "OK",
    "role-known-name-pending": "AMBER",
    unclear: "SOFT_STOP",
  },
};

export function resolveQ93Flag(
  questionKey: string,
  selectedOptionKey: string | null,
): FlagType | null {
  if (!selectedOptionKey) return null;
  const table = Q93_FLAG_BY_OPTION_KEY[questionKey];
  if (!table) return null;
  return table[selectedOptionKey] ?? null;
}

export function isQ93Subq(questionKey: string): questionKey is Q93SubqKey {
  return (Q93_SUBQ_KEYS as readonly string[]).includes(questionKey);
}

export interface Q93Summary {
  flags: Record<Q93SubqKey, FlagType | null>;
  softStopCount: number;
  amberCount: number;
  hasSoftStop: boolean;
  hasAmber: boolean;
  allAnswered: boolean;
  ruleEsc1Eligible: Q93SubqKey[];
}

export function summarizeQ93(
  answers: Partial<Record<Q93SubqKey, string | null>>,
): Q93Summary {
  const flags: Record<Q93SubqKey, FlagType | null> = {
    cp9_paper_procurement_contact: resolveQ93Flag(
      "cp9_paper_procurement_contact",
      answers.cp9_paper_procurement_contact ?? null,
    ),
    cp9_redline_cycle_status: resolveQ93Flag(
      "cp9_redline_cycle_status",
      answers.cp9_redline_cycle_status ?? null,
    ),
    cp9_security_it_review: resolveQ93Flag(
      "cp9_security_it_review",
      answers.cp9_security_it_review ?? null,
    ),
    cp9_signing_authority: resolveQ93Flag(
      "cp9_signing_authority",
      answers.cp9_signing_authority ?? null,
    ),
  };
  const values = Object.values(flags);
  const softStopCount = values.filter((f) => f === "SOFT_STOP").length;
  const amberCount = values.filter((f) => f === "AMBER").length;
  const ruleEsc1Eligible = (Object.keys(flags) as Q93SubqKey[]).filter(
    (k) => flags[k] === "SOFT_STOP",
  );
  return {
    flags,
    softStopCount,
    amberCount,
    hasSoftStop: softStopCount > 0,
    hasAmber: amberCount > 0,
    allAnswered: values.every((f) => f !== null),
    ruleEsc1Eligible,
  };
}
